"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useEnquirySubmit } from "@/hooks/useEnquirySubmit";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  clubName: string;
}

export function ContactFormDialog({
  open,
  onOpenChange,
  clubId,
  clubName,
}: ContactFormDialogProps) {
  const t = useTranslations("contactForm");
  const { status, errorMessage, submit, reset } = useEnquirySubmit({
    path: "/api/contact",
    mapError: async (response) => {
      const data = await response.json();
      return data.error || t("error");
    },
    networkError: t("error"),
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submit({
      ...formData,
      clubId,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after animation completes
    setTimeout(() => {
      reset();
      setFormData({ name: "", email: "", phone: "", message: "" });
    }, 200);
  };

  // Success state
  if (status === "success") {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <DialogHeader>
              <DialogTitle className="text-center">
                {t("successTitle")}
              </DialogTitle>
              <DialogDescription className="text-center mt-2">
                {t("successMessage", {
                  clubName,
                  email: formData.email,
                })}
              </DialogDescription>
            </DialogHeader>
            <p className="mt-4 text-sm text-muted-foreground">
              {t("successNote")}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("contactInfo")}{" "}
              <a
                href="https://www.spond.com/contact/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {t("contactLink")}
              </a>
            </p>
            <Button onClick={handleClose} className="mt-6">
              {t("done")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("titleWithClub", {
              clubName,
            })}
          </DialogTitle>
          <DialogDescription>{t("subtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t("name")}</Label>
            <Input
              id="name"
              name="name"
              placeholder={t("namePlaceholder")}
              value={formData.name}
              onChange={handleChange}
              maxLength={100}
              required
              disabled={status === "submitting"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t("email")}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder={t("emailPlaceholder")}
              value={formData.email}
              onChange={handleChange}
              maxLength={255}
              required
              disabled={status === "submitting"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">{t("phone")}</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder={t("optional")}
              value={formData.phone}
              onChange={handleChange}
              maxLength={50}
              disabled={status === "submitting"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">{t("message")}</Label>
            <Textarea
              id="message"
              name="message"
              placeholder={t("messagePlaceholder")}
              value={formData.message}
              onChange={handleChange}
              maxLength={2000}
              rows={3}
              disabled={status === "submitting"}
            />
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive">{errorMessage}</p>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={status === "submitting"}
            >
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? t("sending") : t("send")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
